import React from 'react';

import { IDateBlockerChecker } from '@private/datepicker';
import { format } from 'date-fns/format';
import { isAfter } from 'date-fns/isAfter';

import DatePickerInput, { IDatePickerInput } from 'components/Controls/DatePickerInput';
import { isFilterNumberRange } from 'components/Grids/DataGrid/Filters/utils/helpers';
import {
  FILTER_OPERATIONS,
  IFilterValue,
  INumberRange,
  ISelectFilterProps,
} from 'components/Grids/DataGrid/Filters/utils/types';
import { FILTRATION_TYPES } from 'constants/common';
import { appReactMemo } from 'hocs';
import { INullable } from 'utils/types';

interface IZonedDateTimePicker extends Omit<IDatePickerInput, 'onChange' | 'value'> {
  availableDaysChecker?: IDateBlockerChecker;
  columnKey: string;
  initialTimezone?: string;
  onSelectFilter: (filter: ISelectFilterProps) => void;
  operation: INullable<FILTER_OPERATIONS>;
  type: string;
  value?: IFilterValue;
}

const ZonedDateTimePicker = ({
  availableDaysChecker,
  columnKey,
  initialTimezone,
  onSelectFilter,
  operation,
  type,
  value,
  ...props
}: IZonedDateTimePicker) => {
  const onDateSelect = React.useCallback(
    (from: string, to: string) => {
      if (!to && !from) {
        return onSelectFilter({ columnKey, operation, value: undefined });
      }

      const formattedFrom =
        type === FILTRATION_TYPES.LOCAL_DATE && from
          ? format(new Date(from), 'yyyy-MM-dd')
          : from;

      if (operation !== FILTER_OPERATIONS.RANGE) {
        if (formattedFrom !== (value as INumberRange)?.min) {
          onSelectFilter({ columnKey, operation, value: formattedFrom });
        }

        return;
      }

      const formattedTo =
        type === FILTRATION_TYPES.LOCAL_DATE && to
          ? format(new Date(to), 'yyyy-MM-dd')
          : to;

      if (
        formattedFrom !== (value as INumberRange)?.min ||
        formattedTo !== (value as INumberRange)?.max
      ) {
        return onSelectFilter({
          columnKey,
          operation,
          value: { max: formattedTo, min: formattedFrom },
        });
      }
    },
    [type, operation, value, onSelectFilter, columnKey],
  );

  const calendarDateChecker: IDateBlockerChecker = React.useCallback(
    (props) => {
      const isDayAfterToday = isAfter(props.date, Date.now());
      if (isDayAfterToday) {
        return true;
      }

      return !!availableDaysChecker?.(props);
    },
    [availableDaysChecker],
  );

  const datePickerValue = React.useMemo(() => {
    if (!value) {
      return;
    }

    if (isFilterNumberRange(value)) {
      if (value.min || value.max) {
        return {
          endDate: value.max,
          startDate: value.min,
        };
      }

      return;
    }

    return value as string;
  }, [value]);

  return (
    <DatePickerInput
      {...props}
      initialTimeZone={initialTimezone}
      isDateBlockedChecker={calendarDateChecker}
      onChange={onDateSelect}
      value={datePickerValue}
    />
  );
};

export default appReactMemo(ZonedDateTimePicker);
